import { useEffect } from 'react'

import { useCreation, useRequest } from 'ahooks'
import { isNil } from 'lodash'

import { KindDetail } from '@eastcoal/api/system/interface'
import { queryKindRootNodesApi } from '@eastcoal/api/system/kind/queryKindRootNodes'

import { KeySet } from '@eastcoal/common/types/utilities'

import { useKindRootNodeStoreApi } from '../hooks/useStore'
import { KindRootNodeAction, KindRootNodeState } from '../store/interface'

import { KindRootNodeStoreUpdaterProps } from './interface'

const useStoreUpdater = <M, K extends KeySet<KindRootNodeState<M>>>(
  key: K,
  value: KindRootNodeState<M>[K]
) => {
  const { setState } = useKindRootNodeStoreApi()

  useEffect(() => {
    setState({ [key]: value })
  }, [key, value, setState])
}

const useStoreSetter = <M, K extends KeySet<KindRootNodeAction<M>>>(
  setter: K,
  value: Parameters<KindRootNodeAction<M>[K]>[0]
) => {
  const { getState } = useKindRootNodeStoreApi()

  useEffect(() => {
    getState()[setter](value as any)
  }, [setter, value, getState])
}

const EMPTY_ARRAY: KindDetail[] = []

export const KindRootNodeStoreUpdater = <M>({
  memberType,
  multiple,
  value,
  onChange
}: KindRootNodeStoreUpdaterProps<M>) => {
  const {
    data = EMPTY_ARRAY,
    error,
    loading,
    refreshAsync
  } = useRequest(queryKindRootNodesApi, {
    manual: false
  })

  const filteredData = useCreation(() => {
    return !isNil(memberType)
      ? data.filter(
          ({ memberTypes }) => memberTypes && memberTypes.includes(memberType)
        )
      : data
  }, [data, memberType])

  useStoreUpdater('data', filteredData)
  useStoreUpdater('error', error)
  useStoreUpdater('loading', loading)
  useStoreUpdater('reload', refreshAsync)

  useStoreUpdater('multiple', multiple)
  useStoreUpdater<M, 'onChange'>('onChange', onChange)

  useStoreSetter<M, 'setValue'>('setValue', value)

  return null
}
